'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { fetchReferencesCategories } from '@eliadmin/core/src/api/references/references-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form, FormField, FormItem, FormMessage } from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { DialogClose } from '@eliadmin/core/src/components/ui/dialog';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { Field } from '@eliadmin/core/src/components/form/field';
import Link from '@eliadmin/core/src/components/link';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useCallback, useState } from 'react';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    custom: z.object({
      interested_in: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

interface ContactFormProps {
  className?: string;
  inDialog?: boolean;
  onSuccess?: () => void;
}

export function ContactForm({ className, inDialog = false, onSuccess }: ContactFormProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      custom: { interested_in: '' },
      text: '',
      gdpr: true,
    },
  });

  const { mutateAsync } = useMutation({ mutationFn: sendContactMessage });
  const router = useRouter();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const toggleCategory = useCallback(
    (categoryName: string) => {
      setSelectedCategories(prev => {
        const next = prev.includes(categoryName)
          ? prev.filter(item => item !== categoryName)
          : [...prev, categoryName];

        form.setValue('custom.interested_in', next.join(', '), { shouldValidate: true });
        return next;
      });
    },
    [form],
  );

  const { data: referenceCategories = [], isLoading } = useQuery({
    queryKey: ['REFERENCE_CATEGORY', locale],
    queryFn: () => fetchReferencesCategories({ locale }).then(r => r.data),
  });

  const handleSubmit = form.handleSubmit(async data => {
    console.log('Submitting contact form with data:', data);

    try {

      await mutateAsync({ data: { ...data,  }, locale });

      if (inDialog) {
        onSuccess?.();
      } else {
        toast({ title: t('text658'), variant: 'success' });
        router.push(ROUTES.HOME());
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        Object.entries(err.fields).forEach(([field, errors]) => {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        });
        return;
      }

      if (!inDialog) {
        toast({ title: t('text657'), description: (err as any)?.message, variant: 'destructive' });
      }
    }
  });

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-6 rounded-[8px] bg-[#F7EEDD] sm:gap-8 lg:gap-12',
        className,
      )}
    >
      <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-6">
        <div className="flex w-full items-start justify-between">
          <Heading title={t('text1117')} tag="h2" />
          {inDialog && (
            <DialogClose className="cursor-pointer transition-all hover:opacity-75">
              <Image
                src="/images/icons/close-black.svg"
                alt={t('text1855')}
                width={0}
                height={0}
                className="h-5 w-5 sm:h-6 sm:w-6"
              />
            </DialogClose>
          )}
        </div>
        <Paragraph color="muted-foreground" size="md">
          {t('text3371')}
        </Paragraph>
      </div>

      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className={cn(
            'flex w-full flex-col items-start justify-start gap-6 sm:gap-8 lg:gap-12',
            'ea-contact-form__form',
          )}
        >
          <div
            className={cn(
              'grid w-full grid-cols-12 gap-4 md:gap-5 lg:gap-6',
              'ea-contact-form__fields-wrapper',
            )}
          >
            <Field
              required
              control={form.control}
              name="name"
              placeholder="Meno / Názov spoločnosti *"
              className={{ wrapper: 'col-span-12 w-full rounded-none' }}
            />
            <Field
              required
              control={form.control}
              name="email"
              type="email"
              placeholder="E-mail *"
              className={{ wrapper: 'col-span-12 w-full rounded-none sm:col-span-6' }}
            />
            <Field
              control={form.control}
              name="phone"
              placeholder={t('text3559')}
              className={{ wrapper: 'col-span-12 w-full rounded-none sm:col-span-6' }}
            />

            {/* Categories */}
            <div className="col-span-12 flex w-full flex-col items-start justify-start gap-[6px] sm:gap-2 lg:gap-3">
              <Paragraph size="md" color="muted-foreground">
                Mám záujem o:
              </Paragraph>
              {isLoading ? (
                <Spinner />
              ) : (
                <div className="flex w-full flex-wrap items-start justify-start gap-2">
                  {referenceCategories.map(category => {
                    const isSelected = selectedCategories.includes(category.name);

                    return (
                      <div
                        key={category.name}
                        onClick={() => toggleCategory(category.name)}
                        className={cn(
                          'flex h-[42px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[8px] border border-black/15 pl-3 pr-4 text-sm font-normal text-black transition-all hover:border-white/50 hover:bg-white/50 sm:h-[45px] sm:pl-4 sm:pr-5 sm:text-base lg:pl-[22px] lg:pr-6',
                          isSelected && 'border-white bg-white',
                        )}
                      >
                        <Image
                          src={category?.image?.url ?? ''}
                          alt={resolveImageAlt(category?.image, category?.name)}
                          width={20}
                          height={20}
                          className="max-h-5 min-h-5 min-w-5 max-w-5 object-contain"
                        />
                        {category.name}
                      </div>
                    );
                  })}
                </div>
              )}
              {form.formState.errors.custom?.interested_in && (
                <p className="mt-2 w-full text-sm text-red-600">
                  Musíte zvoliť aspoň jednu kategóriu.*
                </p>
              )}
            </div>

            <Field
              control={form.control}
              name="text"
              type="textarea"
              placeholder={t('text2340')}
              className={{ wrapper: 'col-span-12 w-full rounded-none' }}
            />
          </div>

          {/* Footer */}
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Paragraph size="md" color="muted-foreground">
              Odoslaním formuláru{' '}
              <Link
                prefetch={false}
                href="/ochrana-osobnych-udajov"
                target="_blank"
                className={cn('ea-custom-underline')}
              >
                súhlasíte so spracovaním osobných údajov
              </Link>{' '}
              {t('text3354')}
            </Paragraph>

            <FormField
              control={form.control}
              name="g-recaptcha-response"
              render={() => (
                <FormItem required className="col-span-12">
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              variant="dark"
              onClick={() => console.log(form.formState.errors)}
              className={cn('w-full rounded-full sm:w-fit', 'ea-contact-form__submit')}
              isLoading={form.formState.isSubmitting}
            >
              ODOSLAŤ
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
